/**
 * useCommHub - React hook for Comm Hub WebSocket connection
 *
 * Drop this file into any Electron app's src/hooks/ directory.
 * Provides real-time connection to the Quantrolix Comm Hub (port 3030).
 *
 * Usage:
 *   const { connected, pendingTasks, activeAgents, councilSessions } = useCommHub('fire-pos');
 */
import { useState, useEffect, useRef, useCallback } from 'react';

const COMM_HUB_WS_URL = 'wss://apps.quantrolix.ai/comm-hub/ws';
const RECONNECT_DELAY = 5000;
const MAX_RECONNECT_DELAY = 30000;

export function useCommHub(project = '') {
  const [connected, setConnected] = useState(false);
  const [pendingTasks, setPendingTasks] = useState(0);
  const [activeAgents, setActiveAgents] = useState(0);
  const [councilSessions, setCouncilSessions] = useState(0);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const reconnectDelayRef = useRef(RECONNECT_DELAY);
  const reconnectTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const fetchInitialState = useCallback(async () => {
    try {
      const [tasksRes, activityRes] = await Promise.allSettled([
        fetch('https://apps.quantrolix.ai/comm-hub/api/tasks'),
        fetch('https://apps.quantrolix.ai/comm-hub/api/activity'),
      ]);

      if (tasksRes.status === 'fulfilled' && tasksRes.value.ok) {
        const tasks = await tasksRes.value.json();
        const pending = tasks.filter(t => t.status === 'pending');
        if (mountedRef.current) setPendingTasks(pending.length);
      }

      if (activityRes.status === 'fulfilled' && activityRes.value.ok) {
        const activities = await activityRes.value.json();
        const active = Array.isArray(activities)
          ? activities.filter(a => {
              const lastUpdate = new Date(a.lastUpdate || a.updatedAt).getTime();
              return Date.now() - lastUpdate < 5 * 60 * 1000; // active in last 5 min
            })
          : [];
        if (mountedRef.current) setActiveAgents(active.length);
      }
    } catch {
      // Comm Hub not running - that's okay
    }

    try {
      const councilRes = await fetch('https://apps.quantrolix.ai/comm-hub/api/council/sessions');
      if (councilRes.ok) {
        const sessions = await councilRes.json();
        const activeSessions = Array.isArray(sessions)
          ? sessions.filter(s => s.status !== 'completed' && s.status !== 'cancelled')
          : [];
        if (mountedRef.current) setCouncilSessions(activeSessions.length);
      }
    } catch {
      // Council endpoint may not exist
    }
  }, []);

  const connectRef = useRef(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const params = new URLSearchParams({ claudeId: `app-${project || 'unknown'}` });
      if (project) params.set('project', project);
      const ws = new WebSocket(`${COMM_HUB_WS_URL}?${params}`);

      ws.onopen = () => {
        if (!mountedRef.current) { ws.close(); return; }
        setConnected(true);
        reconnectDelayRef.current = RECONNECT_DELAY;
        fetchInitialState();
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;
        try {
          const msg = JSON.parse(event.data);
          setLastEvent(msg);

          if (msg.type === 'task_update') {
            fetchInitialState(); // refresh counts
          } else if (msg.type === 'activity_update') {
            fetchInitialState();
          } else if (msg.type === 'agent_joined' || msg.type === 'agent_left') {
            fetchInitialState();
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setConnected(false);
        wsRef.current = null;
        // Reconnect with exponential backoff
        reconnectTimerRef.current = setTimeout(() => {
          if (mountedRef.current && connectRef.current) {
            reconnectDelayRef.current = Math.min(
              reconnectDelayRef.current * 1.5,
              MAX_RECONNECT_DELAY
            );
            connectRef.current();
          }
        }, reconnectDelayRef.current);
      };

      ws.onerror = () => {
        // onclose will fire after this
      };

      wsRef.current = ws;
    } catch {
      // WebSocket constructor can throw if URL is invalid
      setConnected(false);
    }
  }, [project, fetchInitialState]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  useEffect(() => {
    mountedRef.current = true;
    const timer = setTimeout(() => {
      if (mountedRef.current) connect();
    }, 0);

    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { connected, pendingTasks, activeAgents, councilSessions, lastEvent };
}

export default useCommHub;
